/**
 * Detects placeholder addresses patients type when they don't want to give an
 * email (noemail@gmail.com, none@none.com, test@test.com, ...).
 *
 * Local parts are compared after lowercasing, dropping any "+tag", and removing
 * separators and trailing digits, so "No.Email+1@gmail.com", "no_email@yahoo.com"
 * and "noemail123@gmail.com" all normalize to "noemail".
 */

const PLACEHOLDER_LOCAL_PARTS = new Set([
  "abc",
  "abcd",
  "anonymous",
  "asdf",
  "declined",
  "donotemail",
  "donotreply",
  "dontemail",
  "dontreply",
  "dummy",
  "email",
  "example",
  "fake",
  "fakeemail",
  "fakemail",
  "idk",
  "invalid",
  "na",
  "no",
  "noaddress",
  "nobody",
  "noemail",
  "noemailaddress",
  "noemailprovided",
  "noemails",
  "nomail",
  "noname",
  "none",
  "nope",
  "noreply",
  "notapplicable",
  "nothanks",
  "notprovided",
  "null",
  "patient",
  "placeholder",
  "qwerty",
  "refused",
  "sample",
  "test",
  "testemail",
  "testing",
  "unknown",
  "user",
]);

const PLACEHOLDER_DOMAINS = new Set([
  "domain.com",
  "email.test",
  "example.com",
  "example.net",
  "example.org",
  "fake.com",
  "fakeemail.com",
  "noemail.com",
  "nomail.com",
  "none.com",
  "test.com",
  // Disposable inboxes
  "10minutemail.com",
  "guerrillamail.com",
  "mailinator.com",
  "sharklasers.com",
  "temp-mail.org",
  "tempmail.com",
  "trashmail.com",
  "yopmail.com",
]);

const RESERVED_TLDS = new Set(["example", "invalid", "localhost", "test"]);

function normalizeLocalPart(localPart: string): string {
  return localPart
    .split("+")[0]
    .replace(/[._-]/g, "")
    .replace(/\d+$/, "");
}

export function isPlaceholderEmail(email: string): boolean {
  const trimmed = email.trim().toLowerCase();
  const atIndex = trimmed.lastIndexOf("@");
  if (atIndex <= 0) return false;

  const localPart = trimmed.slice(0, atIndex);
  const domain = trimmed.slice(atIndex + 1);
  const normalizedLocal = normalizeLocalPart(localPart);
  const tld = domain.split(".").pop() ?? "";

  if (normalizedLocal === "" && localPart.replace(/[._-]/g, "").length < 5) return true; // 123@gmail.com
  if (PLACEHOLDER_LOCAL_PARTS.has(normalizedLocal)) return true;
  if (/^(.)\1+$/.test(normalizedLocal)) return true; // aaa@, xxxx@
  if (PLACEHOLDER_DOMAINS.has(domain)) return true;
  if (RESERVED_TLDS.has(tld)) return true;
  return false;
}
